import _ from 'lodash';
import { nanoid } from 'nanoid';

export interface EntityCollection<T, Key extends string | number = string> {
    ids: Key[];
    entities: Record<Key, T>;
}

export function repeat<T>(times: number, itemGenerator: (key: React.Key) => T) {
    return _.range(0, times).map(() => itemGenerator(nanoid()));
}

export function entityCollection<T, Key extends string | number>(items: T[], getId: (arg: T) => Key) {
    return items.reduce(
        (acc, curr) => {
            const id = getId(curr);
            acc.ids.push(id);
            acc.entities[id] = curr;
            return acc;
        },
        {
            ids: [] as Key[],
            entities: {} as Record<Key, T>
        }
    );
}

export const shallowCompare = (obj1: Record<string, any>, obj2: Record<string, any>) =>
    !!obj1 &&
    !!obj2 &&
    Object.keys(obj1).length === Object.keys(obj2).length &&
    Object.keys(obj1).every(key => obj1[key] === obj2[key]);

export function getSortedCalls<T, Key extends string>(
    unsortedCalls: T[],
    getId: (arg: T) => Key
): {
    onholdCallsIds: string[];
    notOnHoldCallsIds: string[];
} {
    const notOnHoldCallIdList: string[] = [];
    const onHoldCallIdList: string[] = [];

    const groups = _.groupBy(unsortedCalls, 'onHold');
    let onholdGroup, notOnHoldGroup;
    for (const key in groups) {
        if (key === 'false') {
            notOnHoldGroup = groups[key];
        } else {
            onholdGroup = groups[key];
        }
    }

    const notOnHoldGroupByProgress = _.groupBy(notOnHoldGroup, 'progress');
    let notOnHoldEstablished, notOnHoldInitiating;
    for (const key in notOnHoldGroupByProgress) {
        if (key === 'established') {
            notOnHoldEstablished = notOnHoldGroupByProgress[key];
        } else {
            notOnHoldInitiating = notOnHoldGroupByProgress[key];
        }
    }

    const notOnHoldInitiatingGroupByDirection = _.groupBy(notOnHoldInitiating, 'direction');
    let notOnHoldInitiatingOutbound, notOnHoldInitiatingInbound;
    for (const key in notOnHoldInitiatingGroupByDirection) {
        if (key === 'inbound') {
            notOnHoldInitiatingInbound = notOnHoldInitiatingGroupByDirection['inbound'];
        } else {
            notOnHoldInitiatingOutbound = notOnHoldInitiatingGroupByDirection['outbound'];
        }
    }

    // #1 - incoming calls notOnHoldGroupByProgressInitiatingInbound {direction: "inbound", progress: "initiating", onhold: false}
    if (notOnHoldInitiatingInbound && notOnHoldInitiatingInbound.length) {
        const sortedNotOnHoldInitiatingInbound = _.orderBy(notOnHoldInitiatingInbound, ['initiated'], ['desc']);
        sortedNotOnHoldInitiatingInbound.forEach(call => {
            const id = getId(call);
            notOnHoldCallIdList.push(id);
        });
    }

    // #2 - new outbound calls notOnHoldGroupByProgressInitiatingOutbound {direction: "outbound", progress: "initiating", onhold: false}
    if (notOnHoldInitiatingOutbound && notOnHoldInitiatingOutbound.length) {
        const sortedNotOnHoldInitiatingOutbound = _.orderBy(notOnHoldInitiatingOutbound, ['initiated'], ['desc']);
        sortedNotOnHoldInitiatingOutbound.forEach(call => {
            const id = getId(call);
            notOnHoldCallIdList.push(id);
        });
    }

    // #3 - notOnHoldEstablished calls in progress meaning {progress: "established", onhold: false}
    if (notOnHoldEstablished && notOnHoldEstablished.length) {
        const sortedNotOnHoldEstablished = _.orderBy(notOnHoldEstablished, ['initiated'], ['desc']);
        sortedNotOnHoldEstablished.forEach(call => {
            const id = getId(call);
            notOnHoldCallIdList.push(id);
        });
    }

    // sortedOnHoldGroup - #4 sorted on hold calls
    if (onholdGroup && onholdGroup.length) {
        const sortedOnHoldGroup = _.orderBy(onholdGroup, ['initiated'], ['desc']);
        sortedOnHoldGroup.forEach(call => {
            const id = getId(call);
            onHoldCallIdList.push(id);
        });
    }

    const sortedCallObj: {
        onholdCallsIds: string[];
        notOnHoldCallsIds: string[];
    } = {
        onholdCallsIds: onHoldCallIdList,
        notOnHoldCallsIds: notOnHoldCallIdList
    };

    return sortedCallObj;
}
